import { useLayoutEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { TravelGuide } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { isApiError } from "@/shared/api/errors";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { checkTravelResponse, scopeFailure, travelScopeKey, useTravelScope, type CurrentTravel, type TravelScope } from "./travelPlan/currentScope";
import type { Read } from "./travelPlan/data";

export type GuideRead<T> = Read<T>;
type Authority = ReturnType<typeof useTravelScope>;
const marker = "guide-current-scope";
// Provenance is local to actual completed guarded reads. Never a DTO identity,
// guessed epoch, retimed confirmation or permission granted by a cache key.
// Store only detached data-property snapshots. Cache identities locate evidence;
// cache value/scope/confirmed and caller serialization never supply that evidence.
const completed = new WeakMap<object, CurrentTravel<unknown>>();
function detached<T>(value: T): T {
  const active = new WeakSet<object>();
  let nodes = 0;
  const copy = (input: unknown, depth: number): unknown => {
    if (++nodes > 20_000 || depth > 64) throw scopeFailure();
    if (input === null || input === undefined || typeof input === "string" || typeof input === "boolean") return input;
    if (typeof input === "number" && Number.isFinite(input)) return input;
    if (typeof input !== "object" || active.has(input)) throw scopeFailure();
    const array = Array.isArray(input), prototype = Object.getPrototypeOf(input);
    if (prototype !== (array ? Array.prototype : Object.prototype) && !(prototype === null && !array)) throw scopeFailure();
    // No invocation of accessors, toJSON, iterators or arbitrary inherited data.
    if (prototype && (Object.getOwnPropertyDescriptor(prototype, "toJSON")
      || (array && Object.getOwnPropertyDescriptor(Object.prototype, "toJSON")))) throw scopeFailure();
    const fields = Object.getOwnPropertyDescriptors(input);
    const keys = Reflect.ownKeys(fields);
    if (keys.some(key => typeof key !== "string")) throw scopeFailure();
    const length = array ? fields.length?.value : undefined;
    if (array && (!Number.isSafeInteger(length) || length < 0 || length > 20_000 || keys.length !== length + 1)) throw scopeFailure();
    active.add(input);
    const result: Record<string, unknown> | unknown[] = array ? [] : Object.create(null);
    for (const key of keys as string[]) {
      const field = fields[key];
      if (!field || !("value" in field) || key === "toJSON") throw scopeFailure();
      if (array && key === "length") continue;
      if (!field.enumerable || (array && (!/^(0|[1-9][0-9]*)$/.test(key) || Number(key) >= length))) throw scopeFailure();
      Object.defineProperty(result, key, { value: copy(field.value, depth + 1), enumerable: true, writable: false, configurable: false });
    }
    active.delete(input);
    return Object.freeze(result);
  };
  return copy(value, 0) as T;
}
function remember<T>(value: CurrentTravel<T>, validate: (snapshot: CurrentTravel<T>) => void): CurrentTravel<T> {
  const snapshot = detached(value);
  if (!snapshot.scope || !Number.isSafeInteger(snapshot.confirmed) || snapshot.confirmed < 0
    || !Number.isSafeInteger(snapshot.scope.householdVersion) || snapshot.scope.householdVersion < 0
    || ![snapshot.scope.userId, snapshot.scope.petId, snapshot.scope.householdId, snapshot.scope.homeId].every(id => typeof id === "string" && id.trim())) throw scopeFailure();
  validate(snapshot);
  completed.set(value, snapshot);
  return value;
}
function trusted<T>(value: unknown): CurrentTravel<T> | undefined {
  // WeakMap lookup cannot call a supplied getter or proxy trap. A cloned/unknown
  // cache object has no provenance, regardless of its key, shape or confirmation.
  return value && typeof value === "object" ? completed.get(value) as CurrentTravel<T> | undefined : undefined;
}
function checkGuide(value: TravelGuide, scope: TravelScope, guideId?: string) {
  if (!value || typeof value !== "object" || Array.isArray(value) || typeof value.guide_id !== "string" || !value.guide_id.trim()
    || (guideId !== undefined && value.guide_id !== guideId)) throw scopeFailure();
  // Frozen guides have no principal/pet/household identity. Bind to the original
  // request plus fresh real authority, not invented fields in the response.
  checkTravelResponse(value, scope);
}
function shown<T>(authority: Authority, query: { isPending: boolean; isError: boolean; error: unknown; data?: CurrentTravel<T> | T }, retry: () => void): GuideRead<T> {
  if (!authority.canRead) return { state: "unavailable" };
  if (authority.error) return { state: "error", error: authority.error, retry: () => void authority.refresh() };
  if (!authority.scope || !authority.current() || query.isPending) return { state: "loading" };
  if (query.isError) return { state: "error", error: query.error, retry: isApiError(query.error) && !query.error.retryable ? undefined : retry };
  const data = trusted<T>(query.data);
  if (query.data && !data) return { state: "error", error: scopeFailure(), retry };
  if (!data?.scope || travelScopeKey(data.scope) !== travelScopeKey(authority.scope) || !(data.confirmed >= authority.confirmed)) return { state: "loading" };
  return { state: "ready", data: data.value };
}
function fixtureRead<T>(query: { isPending: boolean; isError: boolean; error: unknown; data?: CurrentTravel<T> | T }, retry: () => void): GuideRead<T> {
  if (query.isPending) return { state: "loading" };
  if (query.isError) return { state: "error", error: query.error, retry: isApiError(query.error) && !query.error.retryable ? undefined : retry };
  return { state: "ready", data: query.data as T };
}
export function useCurrentGuides(selectedId?: string) {
  const context = useOptionalCurrentHousehold(), services = useServices(), client = useQueryClient(), authority = useTravelScope();
  const fixture = env.dataMode === "fixture", scope = authority.scope;
  const userId = context?.userId, petId = context?.pet?.pet_id, householdId = context?.household?.household_id, homeId = context?.household?.home_id;
  const available = Boolean(userId?.trim() && petId?.trim() && householdId?.trim() && homeId?.trim());
  const identity = JSON.stringify([userId, petId, householdId, homeId]);
  const request = JSON.stringify([identity, selectedId]);
  const [freshRequest, setFreshRequest] = useState<string | null>(null);
  // Missing in an actually completed bound list is only reason to withhold.
  // It never becomes ready list data or authorization for a positive resource.
  const negativeOnly = !fixture && available && Boolean(selectedId) && freshRequest !== request
    && client.getQueryCache().findAll({ queryKey: queryKeys.guidesFor(userId!, petId!) }).some(q => {
      const data = trusted<TravelGuide[]>(q.state.data);
      if (q.state.status !== "success" || q.state.isInvalidated || !data || !Array.isArray(data.value)) return false;
      const cached = data.scope;
      if (cached.userId !== userId || cached.petId !== petId || cached.householdId !== householdId || cached.homeId !== homeId
        || q.queryKey[4] !== "reader" || q.queryKey[5] !== householdId || q.queryKey[6] !== homeId || q.queryKey[7] !== marker
        || q.queryKey[8] !== cached.householdVersion || (scope && travelScopeKey(cached) !== travelScopeKey(scope))) return false;
      return !data.value.some(guide => guide && typeof guide === "object" && "guide_id" in guide && guide.guide_id === selectedId);
    });
  const retryFresh = () => { setFreshRequest(request); void authority.refresh(); };

  useLayoutEffect(() => {
    if (fixture || !available) return;
    const keys = [queryKeys.session, queryKeys.households(userId!), queryKeys.householdDetail(userId!, householdId!)].map(k => JSON.stringify(k));
    return client.getQueryCache().subscribe(e => {
      if (!keys.includes(JSON.stringify(e.query.queryKey)) || !["updated", "removed"].includes(e.type)) return;
      // useTravelScope immediately rejects this epoch; also cancel these original
      // transport reads, which do not use its travel-prefixed query keys.
      void client.cancelQueries({ predicate: q => q.queryKey[0] === "transport" && q.queryKey.includes(marker) && q.queryKey.includes(userId) && q.queryKey.includes(petId) });
    });
  }, [client, fixture, available, identity]);
  const query = useQuery<CurrentTravel<TravelGuide[]> | TravelGuide[]>({
    queryKey: fixture ? [...queryKeys.guidesFor("fixture", "fixture"), "fixture"] : [...queryKeys.guidesFor(scope?.userId ?? "-", scope?.petId ?? "-"), "reader", scope?.householdId ?? "-", scope?.homeId ?? "-", marker, scope?.householdVersion ?? "-"],
    queryFn: async ({ signal }) => {
      if (fixture) return services.transport.guides(petId, signal);
      const result = await authority.read(signal, () => services.transport.guides(scope!.petId, signal));
      return remember(result, snapshot => {
        if (!Array.isArray(snapshot.value)) throw scopeFailure();
        for (const guide of snapshot.value) checkGuide(guide, snapshot.scope);
      });
    },
    enabled: fixture || (available && Boolean(scope) && authority.current() && !negativeOnly),
    structuralSharing: false,
    ...(fixture ? {} : { retry: false, staleTime: 0, refetchOnMount: true, refetchOnWindowFocus: false, refetchOnReconnect: false }),
  });
  const actual = fixture ? fixtureRead(query, () => void query.refetch()) : available ? shown(authority, query, () => void query.refetch()) : { state: "unavailable" as const };
  const read: GuideRead<TravelGuide[]> = negativeOnly && !authority.error ? { state: "unavailable" } : selectedId && actual.state === "error" && actual.retry ? { ...actual, retry: retryFresh } : actual;
  return { read, authority, negativeOnly, retryFresh };
}
export function useCurrentGuideDetail(authority: Authority, guides: GuideRead<TravelGuide[]>, guideId: string | undefined): GuideRead<TravelGuide> {
  const services = useServices(), fixture = env.dataMode === "fixture", scope = authority.scope;
  const allowed = Boolean(guideId && guides.state === "ready" && guides.data.some(guide => guide.guide_id === guideId));
  const selected = useRef({ allowed, guideId });
  selected.current = { allowed, guideId };
  const query = useQuery<CurrentTravel<TravelGuide> | TravelGuide>({
    queryKey: fixture ? [...queryKeys.guide(guideId ?? "-"), "fixture"] : [...queryKeys.guide(guideId ?? "-"), "reader", scope?.userId ?? "-", scope?.householdId ?? "-", scope?.homeId ?? "-", scope?.petId ?? "-", marker, scope?.householdVersion ?? "-"],
    queryFn: async ({ signal }) => {
      if (fixture) return services.transport.guide(guideId!, signal);
      if (!selected.current.allowed || selected.current.guideId !== guideId) throw scopeFailure();
      const result = await authority.read(signal, () => services.transport.guide(guideId!, signal));
      if (!selected.current.allowed || selected.current.guideId !== guideId || !authority.current()) throw scopeFailure();
      return remember(result, snapshot => checkGuide(snapshot.value, snapshot.scope, guideId));
    },
    enabled: allowed && (fixture || (Boolean(scope) && authority.current())),
    structuralSharing: false,
    ...(fixture ? {} : { retry: false, staleTime: 0, refetchOnMount: true, refetchOnWindowFocus: false, refetchOnReconnect: false }),
  });
  if (!allowed) return { state: "unavailable" };
  return fixture ? fixtureRead(query, () => void query.refetch()) : shown(authority, query, () => void query.refetch());
}
